/**
 * FC001P-49 — Digital Asset Creator.
 *
 * Choose an approved seasonal/campaign template, edit the heading only, and
 * either download a finished asset or send it to print. The original ticket
 * was explicit that this was download-only and in-house with no Transport
 * seam — that scope grew to include a print path, so templates now carry a
 * `printable` flag (theme editor) and only those show Quantity/Size and
 * "Send to print", behind the same window.ThAssetCreatorTransport.submitPrint
 * seam the menu builder uses (see tradehub-print-transport.js). A template
 * with no physical print format (e.g. an Instagram-square social asset)
 * stays download-only, with no Quantity/Size at all.
 *
 * The download itself is still a real client-side PNG render (the template's
 * own uploaded artwork, composited with the heading text, exported with
 * canvas.toBlob), not a stub — the print path renders its own PDF
 * server-side instead of reusing this canvas output, so this file never
 * generates a PDF itself. Each size a printable template offers is its own
 * uploaded artwork file (sc-asset-creator.liquid: artwork_a5/a4/a3), not one
 * file scaled by math — dimensions come straight off whichever file is
 * currently selected (state.template.artworks), so there's nothing here to
 * keep in sync when a merchant swaps a file. A template with no artwork
 * uploaded yet falls back to a plain gradient at a fixed 1080×1080 (an Instagram post), so it
 * can be built and tested before final art exists. Quantity is print-only
 * and has no effect on the PNG (a "quantity" of a single downloaded file
 * doesn't mean anything).
 */
(function () {
  'use strict';

  /* The customer's Top / Middle / Bottom choice: y is where the text block
     sits as a percentage of the artwork height, anchor is the block's own
     vertical offset (0 = hangs from y, -100 = sits on y, -50 = centred). */
  var TEXT_POSITIONS = {
    top: { y: 8, anchor: 0 },
    middle: { y: 50, anchor: -50 },
    bottom: { y: 92, anchor: -100 }
  };

  var GRADIENT_FALLBACK_WIDTH = 1080;
  var GRADIENT_FALLBACK_HEIGHT = 1080;

  /* The four Instagram aspect ratios. A template with no artwork uploaded
     yet offers all four on its placeholder gradient at these sizes; once
     artwork exists, only the ratios with an uploaded file are offered and
     the file's own pixels win (see currentArtwork/exportDims). */
  var INSTAGRAM_FORMATS = [
    { id: '1:1', width: 1080, height: 1080 },
    { id: '4:5', width: 1080, height: 1350 },
    { id: '5:4', width: 1350, height: 1080 },
    { id: '9:16', width: 1080, height: 1920 }
  ];

  function instagramFormat(id) {
    return INSTAGRAM_FORMATS.filter(function (f) { return f.id === id; })[0] || null;
  }

  /* Not exposed as theme settings (the schema is already close to Shopify's
     40-setting soft cap) — these rarely change, unlike the copy strings
     that are settings. Same values sc-menu-builder.liquid defaults to. */
  var LOGO_MAX_KB = 2048;
  var LOGO_MIN_PX = 200;

  /**
   * Promise-wrapped image load, used for both the template's own artwork
   * (a remote CDN url — crossOrigin lets canvas.toBlob read it back without
   * tainting) and an uploaded logo (a data: URL, where crossOrigin is
   * simply ignored — harmless either way).
   */
  function loadImage(url) {
    return new Promise(function (resolve, reject) {
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function () { resolve(img); };
      img.onerror = function () { reject(new Error('image failed to load')); };
      img.src = url;
    });
  }

  /**
   * One key per print submission, generated once and carried through the
   * sessionStorage draft, the same idempotency approach as the menu builder
   * (see newRequestKey in tradehub-menu-builder.js) — a retried or
   * double-fired request must not create a second print order.
   */
  function newRequestKey() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    return 'ac-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
  }

  function AssetCreator(root) {
    this.root = root;
    this.storageKey = root.dataset.storageKey;
    this.emptyTitle = root.dataset.emptyTitle || 'No templates match this filter';
    this.emptyText = root.dataset.emptyText || '';
    this.errorEmpty = root.dataset.errorEmpty || 'Please enter a heading.';
    this.errorLimit = root.dataset.errorLimit || 'That heading is too long for this template.';

    /* Print path — only templates flagged `printable` in the theme editor
       ever reach this; see the printable check in selectTemplate/goPrint.
       Nothing here duplicates the menu builder's own delivery form; it just
       reuses the same field shape so the backend's shared order pipeline can
       treat both the same way. */
    this.loggedIn = root.dataset.loggedIn === 'true';
    this.accountUrl = root.dataset.accountUrl || '';
    this.maxPerOrder = parseInt(root.dataset.maxPerOrder, 10) || 500;
    this.defaultVenue = {
      email: root.dataset.defaultEmail || '',
      venue: root.dataset.defaultVenue || '',
      address: root.dataset.defaultAddress || '',
      postcode: root.dataset.defaultPostcode || '',
      country: root.dataset.defaultCountry || ''
    };

    this.printOptions = root.querySelector('[data-ac-print-options]');
    this.quantityPills = root.querySelector('[data-ac-quantity-pills]');
    this.sizeHeading = root.querySelector('[data-ac-size-heading]');
    this.sizePills = root.querySelector('[data-ac-size-pills]');
    this.formatField = root.querySelector('[data-ac-format-field]');
    this.formatPills = root.querySelector('[data-ac-format-pills]');
    this.posPills = root.querySelector('[data-ac-pos-pills]');
    this.colourSwatches = root.querySelector('[data-ac-colour-swatches]');
    this.colourCustom = root.querySelector('[data-ac-colour-custom]');
    this.printPanel = root.querySelector('[data-ac-print-panel]');
    this.printSubmitBtn = root.querySelector('[data-ac-send-print]');
    this.printSigninNote = root.querySelector('[data-ac-print-signin]');
    this.printError = root.querySelector('[data-ac-print-error]');
    this.printSuccess = root.querySelector('[data-ac-print-success]');
    this.printFailure = root.querySelector('[data-ac-print-failure]');
    this.printOrderRef = root.querySelector('[data-ac-print-order-ref]');

    this.gallerySection = root.querySelector('[data-ac-step="1"]');
    this.split = root.querySelector('[data-ac-split]');
    this.grid = root.querySelector('[data-ac-grid]');
    this.chipsWrap = root.querySelector('[data-ac-chips]');
    this.emptyState = root.querySelector('[data-ac-empty]');
    this.emptyTitleNode = root.querySelector('[data-ac-empty-title]');
    this.emptyTextNode = root.querySelector('[data-ac-empty-text]');

    this.art = root.querySelector('[data-ac-art]');
    this.canvasBox = root.querySelector('[data-ac-canvas-box]');
    this.previewHeading = root.querySelector('[data-ac-preview-heading]');
    this.previewActions = root.querySelector('[data-ac-preview-actions]');
    this.logoPreview = root.querySelector('[data-ac-logo-preview]');

    this.logoField = root.querySelector('[data-ac-logo-field]');
    this.logoInput = root.querySelector('[data-ac-logo]');
    this.logoUploadLabel = root.querySelector('[data-ac-logo-upload-label]');
    this.logoClearBtn = root.querySelector('[data-ac-logo-clear]');
    this.logoError = root.querySelector('[data-ac-logo-error]');

    this.templateNameNode = root.querySelector('[data-ac-template-name]');
    this.templateMetaNode = root.querySelector('[data-ac-template-meta]');
    this.headingInput = root.querySelector('[data-ac-heading]');
    this.counter = root.querySelector('[data-ac-counter]');
    this.hint = root.querySelector('[data-ac-hint]');
    this.errorNode = root.querySelector('[data-ac-error]');
    this.resetBtn = root.querySelector('[data-ac-reset]');
    this.generateBtn = root.querySelector('[data-ac-generate]');

    this.downloadMeta = root.querySelector('[data-ac-download-meta]');
    this.downloadLink = root.querySelector('[data-ac-download-link]');
    this.restartBtn = root.querySelector('[data-ac-restart]');

    this.state = this.blankState();
    this.activeObjectUrl = null;

    this.bind();
    this.load();
  }

  AssetCreator.prototype.blankState = function () {
    return {
      step: 1,
      filter: 'all',
      template: null,
      heading: '',
      requestKey: '',
      quantity: '',
      size: '',
      format: '',
      textPos: '',
      textColor: '',
      logo: null,
      venue: {
        email: this.defaultVenue.email,
        venue: this.defaultVenue.venue,
        address: this.defaultVenue.address,
        postcode: this.defaultVenue.postcode,
        country: this.defaultVenue.country
      }
    };
  };

  /* ------------------------------------------------------------------ */
  /* Persistence                                                         */
  /* ------------------------------------------------------------------ */

  AssetCreator.prototype.save = function () {
    if (!this.storageKey) return;
    try {
      window.sessionStorage.setItem(this.storageKey, JSON.stringify(this.state));
    } catch (err) {
      /* Private browsing or a full quota — the draft just doesn't persist. */
    }
  };

  AssetCreator.prototype.load = function () {
    var restored = null;
    if (this.storageKey) {
      try {
        var raw = window.sessionStorage.getItem(this.storageKey);
        if (raw) restored = JSON.parse(raw);
      } catch (err) {
        restored = null;
      }
    }

    /* The draft keeps the customer's choices, but the template itself is
       re-read from the page. A card that no longer exists (block removed or
       switched off) drops the draft back to the gallery. */
    var restoredBtn = null;
    if (restored && restored.template && this.grid) {
      this.grid.querySelectorAll('[data-ac-select]').forEach(function (btn) {
        if (btn.dataset.templateId === restored.template.id) restoredBtn = btn;
      });
    }

    if (restoredBtn) {
      this.state = Object.assign(this.blankState(), restored);
      this.state.template = this.buildTemplate(restoredBtn.dataset);
      this.renderTemplate();
      this.goTo(this.state.step === 1 ? 2 : this.state.step, true);
    } else {
      this.goTo(1, true);
    }

    if (this.state.filter && this.state.filter !== 'all' && this.chipsWrap) {
      var savedChip = this.chipsWrap.querySelector('[data-ac-chip="' + this.state.filter + '"]');
      if (savedChip) this.setFilter(this.state.filter, savedChip);
    }
  };

  /* ------------------------------------------------------------------ */
  /* Wiring                                                              */
  /* ------------------------------------------------------------------ */

  AssetCreator.prototype.bind = function () {
    var self = this;

    if (this.chipsWrap) {
      this.chipsWrap.addEventListener('click', function (event) {
        var chip = event.target.closest('[data-ac-chip]');
        if (!chip) return;
        self.setFilter(chip.dataset.acChip, chip);
      });
    }

    if (this.grid) {
      this.grid.addEventListener('click', function (event) {
        var btn = event.target.closest('[data-ac-select]');
        if (!btn) return;
        self.selectTemplate(btn.dataset);
      });
    }

    this.root.querySelectorAll('[data-ac-goto]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.goTo(parseInt(btn.dataset.acGoto, 10));
      });
    });

    if (this.headingInput) {
      this.headingInput.addEventListener('input', function () {
        self.state.heading = this.value;
        self.clearError();
        self.updateCounter();
        self.updatePreviewHeading();
        self.save();
      });
    }

    if (this.resetBtn) {
      this.resetBtn.addEventListener('click', function () {
        if (!self.state.template) return;
        self.state.heading = self.state.template.heading;
        self.headingInput.value = self.state.heading;
        self.clearError();
        self.updateCounter();
        self.updatePreviewHeading();
        self.save();
      });
    }

    if (this.generateBtn) {
      this.generateBtn.addEventListener('click', function () {
        self.generate();
      });
    }

    if (this.restartBtn) {
      this.restartBtn.addEventListener('click', function () {
        self.state = self.blankState();
        self.save();
        self.goTo(1);
        var allChip = self.chipsWrap && self.chipsWrap.querySelector('[data-ac-chip="all"]');
        if (allChip) self.setFilter('all', allChip);
      });
    }

    if (this.quantityPills) {
      this.quantityPills.addEventListener('click', function (event) {
        var btn = event.target.closest('[data-ac-quantity]');
        if (!btn) return;
        self.setQuantity(btn.dataset.acQuantity, btn);
        self.clearPrintError();
        self.save();
      });
    }

    if (this.sizePills) {
      this.sizePills.addEventListener('click', function (event) {
        var btn = event.target.closest('[data-ac-size]');
        if (!btn) return;
        self.setSize(btn.dataset.acSize, btn);
        self.clearPrintError();
        self.save();
      });
    }

    if (this.posPills) {
      this.posPills.addEventListener('click', function (event) {
        var btn = event.target.closest('[data-ac-pos]');
        if (!btn) return;
        self.state.textPos = btn.dataset.acPos;
        self.updateHeadingStyle();
        self.save();
      });
    }

    if (this.colourSwatches) {
      this.colourSwatches.addEventListener('click', function (event) {
        var btn = event.target.closest('button[data-ac-colour]');
        if (!btn) return;
        self.state.textColor = btn.dataset.acColour;
        self.updateHeadingStyle();
        self.save();
      });
    }

    if (this.colourCustom) {
      this.colourCustom.addEventListener('input', function () {
        self.state.textColor = self.colourCustom.value;
        self.updateHeadingStyle();
        self.save();
      });
    }

    if (this.formatPills) {
      this.formatPills.addEventListener('click', function (event) {
        var btn = event.target.closest('[data-ac-format]');
        if (!btn) return;
        self.setFormat(btn.dataset.acFormat, btn);
        self.save();
      });
    }

    Array.prototype.forEach.call(this.root.querySelectorAll('[data-ac-venue]'), function (input) {
      input.addEventListener('input', function () {
        self.state.venue[input.dataset.acVenue] = input.value;
        self.clearPrintError();
        self.save();
      });
    });

    if (this.printSubmitBtn) {
      this.printSubmitBtn.addEventListener('click', function () {
        self.sendToPrint();
      });
    }

    if (this.logoInput) {
      this.logoInput.addEventListener('change', function () {
        self.handleLogo(this.files && this.files[0]);
      });
    }

    if (this.logoClearBtn) {
      this.logoClearBtn.addEventListener('click', function () {
        self.state.logo = null;
        if (self.logoInput) self.logoInput.value = '';
        self.clearLogoError();
        self.renderLogoPreview();
        self.save();
      });
    }

    this.root.querySelectorAll('[data-ac-restart-print]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.state = self.blankState();
        self.save();
        self.goTo(1);
        var allChip = self.chipsWrap && self.chipsWrap.querySelector('[data-ac-chip="all"]');
        if (allChip) self.setFilter('all', allChip);
      });
    });
  };

  /* ------------------------------------------------------------------ */
  /* Gallery — filtering                                                 */
  /* ------------------------------------------------------------------ */

  AssetCreator.prototype.setFilter = function (value, chipEl) {
    this.state.filter = value;
    this.save();

    if (this.chipsWrap) {
      this.chipsWrap.querySelectorAll('[data-ac-chip]').forEach(function (chip) {
        var active = chip === chipEl;
        chip.classList.toggle('is-active', active);
        chip.setAttribute('aria-selected', active ? 'true' : 'false');
      });
    }

    var visible = 0;
    this.root.querySelectorAll('[data-ac-card]').forEach(function (card) {
      var match = value === 'all' || card.dataset.category === value;
      card.hidden = !match;
      if (match) visible += 1;
    });

    if (this.emptyState) {
      this.emptyState.hidden = visible !== 0;
      if (visible === 0) {
        if (this.emptyTitleNode) this.emptyTitleNode.textContent = this.emptyTitle;
        if (this.emptyTextNode) this.emptyTextNode.textContent = this.emptyText;
      }
    }
  };

  /* ------------------------------------------------------------------ */
  /* Template selection                                                  */
  /* ------------------------------------------------------------------ */

  /* Builds the template record from a card's data-* attributes. Used on
     selection and again on load, so settings changed in the theme editor
     (heading position and colour, artwork, logo) always win over a stale
     draft in sessionStorage. */
  AssetCreator.prototype.buildTemplate = function (dataset) {
    var artworks = { default: null, sizes: {} };
    try {
      artworks = JSON.parse(dataset.templateArtworks || '{}');
      if (!artworks.sizes) artworks.sizes = {};
      if (!artworks.formats) artworks.formats = {};
    } catch (err) {
      /* Malformed JSON shouldn't be possible (it's built by Liquid, not user
         input), but falls back to "no artwork" rather than throwing. */
    }

    /* Instagram formats are for download-only templates; a printable one
       gets its A5/A4/A3 Size instead. With no artwork anywhere yet, all
       four ratios are offered on the placeholder gradient; otherwise only
       those that actually have a file (1:1 is the default upload). */
    var printable = dataset.templatePrintable === 'true';
    var formats = [];
    if (!printable) {
      var hasAnyArtwork = !!(artworks.default || Object.keys(artworks.sizes).length || Object.keys(artworks.formats).length);
      formats = INSTAGRAM_FORMATS.map(function (f) { return f.id; }).filter(function (id) {
        if (!hasAnyArtwork) return true;
        return id === '1:1' ? !!artworks.default : !!artworks.formats[id];
      });
    }

    return {
      id: dataset.templateId,
      name: dataset.templateName,
      category: dataset.templateCategory,
      format: dataset.templateFormat,
      heading: dataset.templateHeading,
      limit: parseInt(dataset.templateLimit, 10) || 40,
      gradientFrom: dataset.templateGradientFrom,
      gradientTo: dataset.templateGradientTo,
      printable: printable,
      artworks: artworks,
      sizes: Object.keys(artworks.sizes),
      formats: formats,
      headingAlign: dataset.templateHeadingAlign || 'center',
      headingX: parseFloat(dataset.templateHeadingX) || 50,
      headingY: parseFloat(dataset.templateHeadingY) || 50,
      headingW: parseFloat(dataset.templateHeadingW) || 70,
      headingColor: dataset.templateHeadingColor || '#000000',
      headingWeight: dataset.templateHeadingWeight === '700' ? 700 : 900,
      logoEnabled: dataset.templateLogoEnabled === 'true',
      logoX: parseFloat(dataset.templateLogoX) || 50,
      logoY: parseFloat(dataset.templateLogoY) || 15,
      logoW: parseFloat(dataset.templateLogoW) || 25
    };
  };

  AssetCreator.prototype.selectTemplate = function (dataset) {
    this.state.template = this.buildTemplate(dataset);
    this.state.heading = dataset.templateHeading;
    this.state.textPos = '';
    this.state.textColor = '';

    /* A fresh template resets quantity/size rather than carrying over a
       previous template's selection — the previous size label might not
       even be one this template offers. */
    if (this.state.template.id !== (this._lastTemplateId || null)) {
      this.state.quantity = '';
      this.state.size = '';
      this.state.format = '';
    }
    this._lastTemplateId = this.state.template.id;

    this.renderTemplate();
    this.save();
    this.goTo(2);
  };

  AssetCreator.prototype.setQuantity = function (value, btnEl) {
    this.state.quantity = value;
    if (this.quantityPills) {
      this.quantityPills.querySelectorAll('[data-ac-quantity]').forEach(function (btn) {
        var active = btn === btnEl;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-checked', active ? 'true' : 'false');
      });
    }
  };

  AssetCreator.prototype.setSize = function (value, btnEl) {
    this.state.size = value;
    if (this.sizePills) {
      this.sizePills.querySelectorAll('[data-ac-size]').forEach(function (btn) {
        var active = btn === btnEl;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-checked', active ? 'true' : 'false');
      });
    }
    this.updateCanvasRatio();
    this.updateArt();
  };

  /**
   * The artwork actually in play right now: the selected Size's own upload
   * if there is one, else the template's single default upload, else null
   * (meaning: no real artwork — draw the gradient instead).
   */
  AssetCreator.prototype.currentArtwork = function () {
    var t = this.state.template;
    if (!t) return null;
    if (this.state.size && t.artworks.sizes[this.state.size]) return t.artworks.sizes[this.state.size];
    var f = this.state.format;
    if (f && f !== '1:1' && t.artworks.formats && t.artworks.formats[f]) return t.artworks.formats[f];
    return t.artworks.default || null;
  };

  AssetCreator.prototype.setFormat = function (value, btnEl) {
    this.state.format = value;
    if (this.formatPills) {
      this.formatPills.querySelectorAll('[data-ac-format]').forEach(function (btn) {
        var active = btn === btnEl;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-checked', active ? 'true' : 'false');
      });
    }
    this.updateCanvasRatio();
    this.updateArt();
  };

  /** Builds the Format pills from the template's available ratios. The
      field only shows with two or more; a single one is still selected
      (state.format) so currentArtwork picks the right file. */
  AssetCreator.prototype.renderFormatPills = function () {
    var t = this.state.template;
    var formats = t.formats || [];
    if (this.formatField) this.formatField.hidden = formats.length < 2;
    if (!formats.length) {
      this.state.format = '';
      if (this.formatPills) this.formatPills.innerHTML = '';
      return;
    }

    var selected = formats.indexOf(this.state.format) !== -1
      ? this.state.format
      : (formats.indexOf('1:1') !== -1 ? '1:1' : formats[0]);

    var self = this;
    var selectedBtn = null;
    if (this.formatPills) this.formatPills.innerHTML = '';
    formats.forEach(function (id) {
      if (!self.formatPills) return;
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'th-ac__pill th-button-label';
      btn.setAttribute('role', 'radio');
      btn.dataset.acFormat = id;
      btn.textContent = id;
      if (id === selected) selectedBtn = btn;
      self.formatPills.appendChild(btn);
    });
    this.setFormat(selected, selectedBtn);
  };

  /**
   * The pixel dimensions actually exported — read straight off whichever
   * artwork file is currently in play, never computed. Falls back to a
   * fixed size only when no artwork has been uploaded at all yet.
   */
  AssetCreator.prototype.exportDims = function () {
    var art = this.currentArtwork();
    if (art) return { width: art.width, height: art.height };
    var fmt = this.state.format ? instagramFormat(this.state.format) : null;
    if (fmt) return { width: fmt.width, height: fmt.height };
    return { width: GRADIENT_FALLBACK_WIDTH, height: GRADIENT_FALLBACK_HEIGHT };
  };

  AssetCreator.prototype.updateCanvasRatio = function () {
    if (!this.canvasBox) return;
    var dims = this.exportDims();
    this.canvasBox.style.setProperty('--ac-canvas-ratio', dims.width + ' / ' + dims.height);
    /* Plain number too: the stylesheet caps the preview's height (a 9:16
       would otherwise be taller than the screen) and needs w/h to turn
       that height cap back into a width. */
    this.canvasBox.style.setProperty('--ac-canvas-num', (dims.width / dims.height).toFixed(4));
  };

  /** Swaps the live preview's background between real artwork and the
      gradient fallback — called on template select and again on Size
      change, since a different size may have (or lack) its own upload. */
  AssetCreator.prototype.updateArt = function () {
    if (!this.art) return;
    var t = this.state.template;
    var art = this.currentArtwork();
    if (art) {
      this.art.style.background = '';
      this.art.style.backgroundImage = 'url(' + art.url + ')';
    } else {
      this.art.style.backgroundImage = '';
      this.art.style.background = 'linear-gradient(180deg, rgb(' + t.gradientFrom + ') 0%, rgb(' + t.gradientTo + ') 100%)';
    }
  };

  /** Heading position/colour are per-template, not per-size (ISO sizes keep
      the same proportions), so this only needs to run once per selection. */
  /** Where the heading sits vertically, and in what colour. The customer's
      Top / Middle / Bottom and colour choice win; with none made, the
      template's own position and colour (theme editor) apply. Top and
      bottom anchor the text block to the edge so a multi-line heading
      never runs off the artwork; anchor is the block's own vertical
      offset, as a percentage of its height. */
  AssetCreator.prototype.headingLayout = function () {
    var t = this.state.template;
    var pos = TEXT_POSITIONS[this.state.textPos];
    return {
      y: pos ? pos.y : t.headingY,
      anchor: pos ? pos.anchor : -50,
      color: this.state.textColor || t.headingColor
    };
  };

  /** Marks the active position pill and colour swatch. With no choice
      made yet, the pill nearest the template's own position is shown. */
  AssetCreator.prototype.renderTextControls = function () {
    var t = this.state.template;
    var layout = this.headingLayout();
    var activePos = this.state.textPos || (t.headingY < 34 ? 'top' : (t.headingY > 66 ? 'bottom' : 'middle'));
    if (this.posPills) {
      this.posPills.querySelectorAll('[data-ac-pos]').forEach(function (btn) {
        var active = btn.dataset.acPos === activePos;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-checked', active ? 'true' : 'false');
      });
    }
    if (this.colourSwatches) {
      var colour = String(layout.color).toLowerCase();
      var matched = false;
      this.colourSwatches.querySelectorAll('button[data-ac-colour]').forEach(function (btn) {
        var active = btn.dataset.acColour === colour;
        if (active) matched = true;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-checked', active ? 'true' : 'false');
      });
      var customWrap = this.colourSwatches.querySelector('[data-ac-colour-custom-wrap]');
      if (customWrap) {
        customWrap.classList.toggle('is-active', !matched);
        customWrap.style.setProperty('--swatch', colour);
      }
      if (this.colourCustom) this.colourCustom.value = /^#[0-9a-f]{6}$/.test(colour) ? colour : '#ffffff';
    }
  };

  AssetCreator.prototype.updateHeadingStyle = function () {
    if (!this.previewHeading) return;
    var t = this.state.template;
    var layout = this.headingLayout();
    this.previewHeading.style.setProperty('--ac-heading-x', t.headingX + '%');
    this.previewHeading.style.setProperty('--ac-heading-y', layout.y + '%');
    this.previewHeading.style.setProperty('--ac-heading-ty', layout.anchor + '%');
    this.previewHeading.style.setProperty('--ac-heading-w', t.headingW + '%');
    this.previewHeading.style.setProperty('--ac-heading-color', layout.color);
    this.previewHeading.style.setProperty('--ac-heading-align', t.headingAlign || 'center');
    this.previewHeading.style.setProperty('--ac-heading-weight', t.headingWeight || 900);
    this.renderTextControls();
  };

  AssetCreator.prototype.updateLogoStyle = function () {
    if (!this.logoPreview) return;
    var t = this.state.template;
    this.logoPreview.style.setProperty('--ac-logo-x', t.logoX + '%');
    this.logoPreview.style.setProperty('--ac-logo-y', t.logoY + '%');
    this.logoPreview.style.setProperty('--ac-logo-w', t.logoW + '%');
  };

  /* ------------------------------------------------------------------ */
  /* Venue logo upload                                                   */
  /* ------------------------------------------------------------------ */

  AssetCreator.prototype.showLogoError = function (message) {
    if (this.logoError) {
      this.logoError.textContent = message;
      this.logoError.hidden = false;
    }
  };

  AssetCreator.prototype.clearLogoError = function () {
    if (this.logoError) this.logoError.hidden = true;
  };

  /**
   * Mirrors sc-menu-builder.liquid's own handleLogo (FileReader, then an
   * Image() probe to validate real pixel dimensions before accepting) —
   * deliberately not wrapped in a Transport call the way that one is: there
   * is no upload endpoint for either builder yet, and that seam's only
   * current implementation just echoes the data URL straight back, so
   * skipping it here is honest rather than a missing step.
   */
  AssetCreator.prototype.handleLogo = function (file) {
    var self = this;
    if (!file) return;
    this.clearLogoError();

    if (file.type !== 'image/png') {
      this.showLogoError('Please upload a PNG file.');
      if (this.logoInput) this.logoInput.value = '';
      return;
    }
    if (file.size > LOGO_MAX_KB * 1024) {
      this.showLogoError('That file is ' + Math.round(file.size / 1024) + 'kb. The limit is ' + LOGO_MAX_KB + 'kb.');
      if (this.logoInput) this.logoInput.value = '';
      return;
    }

    var reader = new FileReader();
    reader.onload = function () {
      var probe = new Image();
      probe.onload = function () {
        /* Checked here, not just at export time: a logo too small to
           reproduce cleanly should be rejected while the user can still
           pick a different file, not silently baked into a blurry PNG. */
        if (probe.naturalWidth < LOGO_MIN_PX || probe.naturalHeight < LOGO_MIN_PX) {
          self.showLogoError('That image is ' + probe.naturalWidth + '×' + probe.naturalHeight +
            'px. Please upload one at least ' + LOGO_MIN_PX + '×' + LOGO_MIN_PX + 'px.');
          if (self.logoInput) self.logoInput.value = '';
          return;
        }
        self.state.logo = { url: reader.result, name: file.name, width: probe.naturalWidth, height: probe.naturalHeight };
        self.renderLogoPreview();
        self.save();
      };
      probe.onerror = function () {
        self.showLogoError('That file could not be read. Please try again.');
        if (self.logoInput) self.logoInput.value = '';
      };
      probe.src = reader.result;
    };
    reader.onerror = function () {
      self.showLogoError('That file could not be read. Please try again.');
    };
    reader.readAsDataURL(file);
  };

  AssetCreator.prototype.renderLogoPreview = function () {
    var t = this.state.template;
    var has = !!(t && t.logoEnabled && this.state.logo && this.state.logo.url);

    if (this.logoClearBtn) this.logoClearBtn.hidden = !has;
    if (this.logoUploadLabel) this.logoUploadLabel.textContent = has ? (this.state.logo.name || 'Logo uploaded') : 'Upload PNG';

    if (this.logoPreview) {
      this.logoPreview.hidden = !has;
      this.logoPreview.innerHTML = '';
      if (has) {
        var img = document.createElement('img');
        img.src = this.state.logo.url;
        img.alt = '';
        this.logoPreview.appendChild(img);
      }
    }
  };

  AssetCreator.prototype.renderTemplate = function () {
    var t = this.state.template;
    if (!t) return;

    if (this.templateNameNode) this.templateNameNode.textContent = t.name;
    if (this.templateMetaNode) this.templateMetaNode.textContent = t.category + ' · ' + t.format;

    this.updateHeadingStyle();
    this.updateArt();
    this.renderFormatPills();

    if (this.headingInput) {
      this.headingInput.value = this.state.heading;
      this.headingInput.maxLength = t.limit + 20; /* soft cap; validate() enforces the real limit */
    }

    /* Only a template whose artwork was actually laid out with a logo slot
       offers one — see logo_enabled on the template block. */
    if (this.logoField) this.logoField.hidden = !t.logoEnabled;
    if (t.logoEnabled) this.updateLogoStyle();
    this.renderLogoPreview();
    if (this.logoInput) this.logoInput.value = '';
    this.clearLogoError();

    /* Only a template built for a real physical print format offers
       Quantity/Size and "Send to print" — e.g. an Instagram-square social
       template has nothing to print onto, and would have no WooCommerce
       product mapping anyway. */
    if (this.printOptions) this.printOptions.hidden = !t.printable;
    if (this.printPanel) this.printPanel.hidden = !t.printable;
    if (this.printSubmitBtn) this.printSubmitBtn.hidden = !t.printable;

    if (t.printable) {
      this.renderQuantityPills();
      this.renderSizePills();
    }
    this.updateCanvasRatio();

    var self = this;
    Array.prototype.forEach.call(this.root.querySelectorAll('[data-ac-venue]'), function (input) {
      input.value = self.state.venue[input.dataset.acVenue] || '';
    });
    this.clearPrintError();
    if (this.printSuccess) this.printSuccess.hidden = true;
    if (this.printFailure) this.printFailure.hidden = true;

    this.clearError();
    this.updateCounter();
    this.updatePreviewHeading();
  };

  AssetCreator.prototype.renderQuantityPills = function () {
    if (!this.quantityPills) return;
    var buttons = this.quantityPills.querySelectorAll('[data-ac-quantity]');
    if (!buttons.length) return;

    var restored = null;
    buttons.forEach(function (btn) {
      if (btn.dataset.acQuantity === this.state.quantity) restored = btn;
    }, this);

    this.setQuantity(restored ? restored.dataset.acQuantity : buttons[0].dataset.acQuantity, restored || buttons[0]);
  };

  AssetCreator.prototype.renderSizePills = function () {
    if (!this.sizePills || !this.sizeHeading) return;
    var t = this.state.template;
    var sizes = t.sizes || [];

    this.sizeHeading.hidden = sizes.length === 0;
    this.sizePills.hidden = sizes.length === 0;
    this.sizePills.innerHTML = '';
    if (!sizes.length) {
      this.state.size = '';
      return;
    }

    /* Prefer whichever size the format label itself names (e.g. "A4 portrait
       poster" prefers the A4 upload) when it's actually one of the sizes
       uploaded; otherwise just the first uploaded size. */
    var preferred = ['A5', 'A4', 'A3'].filter(function (label) {
      return (t.format || '').indexOf(label) !== -1 && sizes.indexOf(label) !== -1;
    })[0];
    var defaultLabel = preferred || sizes[0];
    var selectedLabel = sizes.indexOf(this.state.size) !== -1 ? this.state.size : defaultLabel;

    var self = this;
    var selectedBtn = null;
    sizes.forEach(function (label) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'th-ac__pill th-button-label';
      btn.setAttribute('role', 'radio');
      btn.dataset.acSize = label;
      btn.textContent = label;
      var active = label === selectedLabel;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-checked', active ? 'true' : 'false');
      if (active) selectedBtn = btn;
      self.sizePills.appendChild(btn);
    });

    this.state.size = selectedLabel;
    if (selectedBtn) this.setSize(selectedLabel, selectedBtn);
  };

  AssetCreator.prototype.updatePreviewHeading = function () {
    if (this.previewHeading) this.previewHeading.textContent = this.state.heading;
  };

  AssetCreator.prototype.updateCounter = function () {
    if (!this.counter || !this.state.template) return;
    var length = (this.state.heading || '').length;
    var limit = this.state.template.limit;
    this.counter.textContent = length + '/' + limit;
    this.counter.classList.toggle('is-limit', length >= limit);
  };

  /* ------------------------------------------------------------------ */
  /* Validation                                                          */
  /* ------------------------------------------------------------------ */

  AssetCreator.prototype.showError = function (message) {
    if (this.errorNode) {
      this.errorNode.textContent = message;
      this.errorNode.hidden = false;
    }
    if (this.hint) this.hint.hidden = true;
    if (this.headingInput) this.headingInput.classList.add('th-ac__input--invalid');
  };

  AssetCreator.prototype.clearError = function () {
    if (this.errorNode) this.errorNode.hidden = true;
    if (this.hint) this.hint.hidden = false;
    if (this.headingInput) this.headingInput.classList.remove('th-ac__input--invalid');
  };

  AssetCreator.prototype.validate = function () {
    var heading = (this.state.heading || '').trim();
    if (!heading) {
      this.showError(this.errorEmpty);
      return false;
    }
    if (this.state.template && heading.length > this.state.template.limit) {
      this.showError(this.errorLimit);
      return false;
    }
    this.clearError();
    return true;
  };

  /* ------------------------------------------------------------------ */
  /* Send to print                                                       */
  /* ------------------------------------------------------------------ */

  AssetCreator.prototype.showPrintError = function (message) {
    if (this.printError) {
      this.printError.textContent = message;
      this.printError.hidden = false;
    }
  };

  AssetCreator.prototype.clearPrintError = function () {
    if (this.printError) this.printError.hidden = true;
  };

  AssetCreator.prototype.validatePrint = function () {
    if (!this.validate()) return false;

    var ok = true;
    var quantity = parseInt(this.state.quantity, 10);
    if (!quantity || quantity < 1) {
      this.showPrintError('Choose a quantity.');
      ok = false;
    } else if (quantity > this.maxPerOrder) {
      this.showPrintError('The maximum is ' + this.maxPerOrder + ' per order.');
      ok = false;
    }
    if (this.state.template && this.state.template.sizes.length && !this.state.size) {
      this.showPrintError('Choose a size.');
      ok = false;
    }

    var required = {
      email: 'Enter a contact email.',
      venue: 'Enter the venue name.',
      address: 'Enter the venue address.',
      postcode: 'Enter the venue postcode.',
      country: 'Enter the country.'
    };
    Object.keys(required).forEach(function (key) {
      if (!String(this.state.venue[key] || '').trim()) {
        this.showPrintError(required[key]);
        ok = false;
      }
    }, this);

    return ok;
  };

  AssetCreator.prototype.printPayload = function () {
    if (!this.state.requestKey) {
      this.state.requestKey = newRequestKey();
      this.save();
    }
    var dims = this.exportDims();
    return {
      requestKey: this.state.requestKey,
      submittedAt: new Date().toISOString(),
      template: {
        id: this.state.template.id,
        name: this.state.template.name,
        gradientFrom: this.state.template.gradientFrom,
        gradientTo: this.state.template.gradientTo
      },
      heading: (this.state.heading || '').trim(),
      quantity: parseInt(this.state.quantity, 10),
      size: this.state.size ? { label: this.state.size, width: dims.width, height: dims.height } : null,
      logo: (this.state.template.logoEnabled && this.state.logo) ? this.state.logo : null,
      venue: this.state.venue
    };
  };

  AssetCreator.prototype.sendToPrint = function () {
    var self = this;

    if (this.printSuccess) this.printSuccess.hidden = true;
    if (this.printFailure) this.printFailure.hidden = true;

    if (!this.state.template || !this.state.template.printable) return;

    if (!this.loggedIn) {
      this.showPrintError('Please sign in before sending this to print.');
      return;
    }

    if (!this.validatePrint()) return;

    /* Guard against a double tap producing two print orders. */
    if (this.printing) return;
    this.printing = true;
    if (this.printSubmitBtn) this.printSubmitBtn.disabled = true;

    this.goTo(3);

    var payload = this.printPayload();

    window.ThAssetCreatorTransport.submitPrint(payload).then(function (result) {
      self.goTo(5);
      if (self.printSuccess) self.printSuccess.hidden = false;
      if (self.printOrderRef) {
        self.printOrderRef.textContent = result && result.orderReference ? result.orderReference : '';
      }
      try {
        window.sessionStorage.removeItem(self.storageKey);
      } catch (e) {
        /* Nothing to clean up if storage was never available. */
      }
    }).catch(function () {
      /* The draft (including requestKey) is deliberately left in place so a
         failed send costs nothing and a retry reuses the same idempotency
         key rather than risking a duplicate order. */
      self.goTo(5);
      if (self.printFailure) self.printFailure.hidden = false;
    }).then(function () {
      self.printing = false;
      if (self.printSubmitBtn) self.printSubmitBtn.disabled = false;
    });
  };

  /* ------------------------------------------------------------------ */
  /* Step navigation                                                     */
  /* ------------------------------------------------------------------ */

  AssetCreator.prototype.goTo = function (step, silent) {
    if (step > 1 && !this.state.template) step = 1;

    this.state.step = step;
    if (!silent) this.save();

    if (this.gallerySection) this.gallerySection.hidden = step !== 1;
    if (this.split) this.split.hidden = step === 1;
    /* Lives in the preview aside (see sc-asset-creator.liquid), not inside
       .th-ac__editor, so the loop below never touches it — the aside stays
       visible across steps 2-4, but the button is only for step 2. */
    if (this.previewActions) this.previewActions.hidden = step !== 2;

    this.root.querySelectorAll('.th-ac__editor [data-ac-step]').forEach(function (section) {
      section.hidden = parseInt(section.dataset.acStep, 10) !== step;
    });

    if (step === 1 && window.scrollY > 0) {
      this.gallerySection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  /* ------------------------------------------------------------------ */
  /* Generate + download                                                 */
  /* ------------------------------------------------------------------ */

  AssetCreator.prototype.generate = function () {
    if (!this.validate()) return;

    var self = this;
    this.goTo(3);

    /* The Figma "Generating your asset…" state is a genuine visual beat,
       not a stub: the canvas render below is near-instant, so a short
       fixed delay is what makes that state readable at all rather than a
       one-frame flash. canvas.toBlob is async, so step 4 only shows once
       the blob (and therefore the download link's href) actually exists —
       advancing on a fixed timer alone would race it. */
    window.setTimeout(function () {
      self.renderAsset(function () {
        self.goTo(4);
      });
    }, 700);
  };

  /**
   * Background (artwork or gradient), then the venue logo, then the heading
   * text — same stacking order as the DOM preview (.th-ac__canvas-art,
   * -logo, -heading, in that order in sc-asset-creator.liquid), composited
   * with two chained async image loads rather than the single one this used
   * before the logo existed. Each stage falls back gracefully rather than
   * leaving the user stuck on the generating screen: a failed artwork load
   * falls back to the gradient, a failed logo load is just skipped.
   */
  AssetCreator.prototype.renderAsset = function (done) {
    var self = this;
    var t = this.state.template;
    var art = this.currentArtwork();
    var dims = this.exportDims();
    var heading = (this.state.heading || '').trim();
    var hasLogo = !!(t.logoEnabled && this.state.logo && this.state.logo.url);

    var canvas = document.createElement('canvas');
    canvas.width = dims.width;
    canvas.height = dims.height;
    var ctx = canvas.getContext('2d');

    function paintGradient() {
      var gradient = ctx.createLinearGradient(0, 0, 0, dims.height);
      gradient.addColorStop(0, 'rgb(' + t.gradientFrom + ')');
      gradient.addColorStop(1, 'rgb(' + t.gradientTo + ')');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, dims.width, dims.height);
    }

    var backgroundReady = art
      ? loadImage(art.url).then(function (img) {
          ctx.drawImage(img, 0, 0, dims.width, dims.height);
        }).catch(paintGradient)
      : (paintGradient(), Promise.resolve());

    var logoReady = backgroundReady.then(function () {
      if (!hasLogo) return;
      return loadImage(self.state.logo.url).then(function (img) {
        var boxW = dims.width * (t.logoW / 100);
        var boxH = boxW * (self.state.logo.height / self.state.logo.width);
        var centerX = dims.width * (t.logoX / 100);
        var centerY = dims.height * (t.logoY / 100);
        ctx.drawImage(img, centerX - boxW / 2, centerY - boxH / 2, boxW, boxH);
      }).catch(function () {
        /* A logo that fails to load (corrupt data URL, browser quirk)
           shouldn't block the rest of the asset — just skip it. */
      });
    });

    /* Canvas text only uses a web font once it has loaded; without this wait
       the first export can fall back to the system sans. */
    var fontReady = logoReady.then(function () {
      var family = getComputedStyle(document.documentElement).getPropertyValue('--font-th-heading-family') || 'sans-serif';
      if (!document.fonts || !document.fonts.load) return;
      return document.fonts.load((t.headingWeight || 900) + ' 100px ' + family, heading || 'A').catch(function () {});
    });

    fontReady.then(function () {
      self.drawHeading(ctx, heading, dims.width, dims.height, t);
      canvas.toBlob(function (blob) {
        self.finishDownload(blob, t, dims);
        if (done) done();
      }, 'image/png');
    });
  };

  AssetCreator.prototype.drawHeading = function (ctx, heading, width, height, t) {
    var family = getComputedStyle(document.documentElement).getPropertyValue('--font-th-heading-family') || 'sans-serif';
    var centerX = width * (t.headingX / 100);
    var layout = this.headingLayout();
    var anchorY = height * (layout.y / 100);
    var maxWidth = width * (t.headingW / 100);
    /* The safe-width box stays centred on headingX; alignment is how the
       text sits inside that box, so a left- or right-aligned heading
       lines up with the box edge rather than floating at its middle. */
    var align = t.headingAlign === 'left' || t.headingAlign === 'right' ? t.headingAlign : 'center';
    var textX = align === 'left' ? centerX - maxWidth / 2 : (align === 'right' ? centerX + maxWidth / 2 : centerX);
    var fontSize = Math.round(width * 0.09);
    var minFontSize = Math.round(width * 0.035);
    var lineHeight, lines;

    function wrap(size) {
      ctx.font = (t.headingWeight || 900) + ' ' + size + 'px ' + family;
      /* Brand tracking: -2% of the font size. */
      if ('letterSpacing' in ctx) ctx.letterSpacing = (-0.02 * size) + 'px';
      lineHeight = size * 1.05;
      var out = [];
      heading.split('\n').forEach(function (paragraph) {
        var words = paragraph.split(' ');
        var current = '';
        words.forEach(function (word) {
          var candidate = current ? current + ' ' + word : word;
          if (ctx.measureText(candidate).width > maxWidth && current) {
            out.push(current);
            current = word;
          } else {
            current = candidate;
          }
        });
        out.push(current);
      });
      return out;
    }

    lines = wrap(fontSize);
    while (lines.length * lineHeight > height * 0.5 && fontSize > minFontSize) {
      fontSize -= 2;
      lines = wrap(fontSize);
    }

    ctx.fillStyle = layout.color || '#000';
    ctx.textAlign = align;
    ctx.textBaseline = 'middle';

    var blockHeight = lines.length * lineHeight;
    var startY = anchorY + blockHeight * (layout.anchor / 100) + lineHeight / 2;
    lines.forEach(function (line, index) {
      ctx.fillText(line, textX, startY + index * lineHeight);
    });
  };

  AssetCreator.prototype.finishDownload = function (blob, t, dims) {
    if (this.activeObjectUrl) URL.revokeObjectURL(this.activeObjectUrl);
    this.activeObjectUrl = URL.createObjectURL(blob);

    var filename = (t.name || 'asset').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') + '.png';

    if (this.downloadLink) {
      this.downloadLink.href = this.activeObjectUrl;
      this.downloadLink.setAttribute('download', filename);
    }

    if (this.downloadMeta) {
      var kb = Math.max(1, Math.round(blob.size / 1024));
      /* t.format (the block's Size / format label) already bakes in its own
         baseline dimensions, e.g. "A4 portrait poster · 2480 × 3508px" — take
         only the descriptive part before that, so the actual exported dims
         (which may differ if a non-baseline Size was picked) aren't shown
         twice. */
      var kind = (t.format || '').split(' · ')[0];
      if (this.state.format && (t.formats || []).length > 1) kind += ' ' + this.state.format;
      this.downloadMeta.textContent = 'PNG · ' + kind + ' · ' + dims.width + ' × ' + dims.height + 'px · ' + kb + ' KB';
    }
  };

  /* ------------------------------------------------------------------ */
  /* Init                                                                */
  /* ------------------------------------------------------------------ */

  function init() {
    document.querySelectorAll('[data-asset-creator]').forEach(function (root) {
      if (root.dataset.acReady) return;
      root.dataset.acReady = 'true';
      new AssetCreator(root);
    });
  }

  if (document.readyState !== 'loading') {
    init();
  } else {
    document.addEventListener('DOMContentLoaded', init);
  }

  document.addEventListener('shopify:section:load', init);
})();
